import React, { useContext, useEffect, useState } from "react";
import { ShopContext } from "../context/ShopContext";
import axios from "axios";
import { toast } from "react-toastify";
import { FaStar, FaRegSmile, FaRegFrown } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import AuthModal from "./Modals/AuthModal";
import SuccessModal from "./Modals/SuccessModal";

const Reviews = ({ productId }) => {
  const { backendUrl, token, reviews } = useContext(ShopContext);
  const [filteredReviews, setFilteredReviews] = useState([]);
  const [activeFilter, setActiveFilter] = useState("popular");
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [hasReviewed, setHasReviewed] = useState(false);
  const [userFeedbackMap, setUserFeedbackMap] = useState({});
  const [showFeedbackThanks, setShowFeedbackThanks] = useState({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [countdown, setCountdown] = useState(10);
  const [isLoading, setIsLoading] = useState(true);
  const [reviewMetrics, setReviewMetrics] = useState({});

  let storedUser = {};
  try {
    storedUser = JSON.parse(localStorage.getItem("user")) || {};
  } catch {
    storedUser = {};
  }

  const username = localStorage.getItem("name") || storedUser.name || "";
  const useremail = localStorage.getItem("email") || storedUser.email || "";

  const productReviews = reviews.filter(review => review.productId === productId);
  const userReview = productReviews.find(review => review.email === useremail);

  useEffect(() => {
    setHasReviewed(!!userReview);
  }, [userReview]);

  const fetchUserFeedback = async () => {
    if (!useremail) return;

    try {
      const reviewIds = reviews.map(review => review._id);
      if (reviewIds.length === 0) return;

      const response = await axios.post(`${backendUrl}/api/reviews/helpful/check-user`, {
        reviewIds,
      }, {
        params: { email: useremail }
      });

      if (response.data.success) {
        setUserFeedbackMap(response.data.data.feedbackMap);
      }
    } catch (error) {
      console.error("Failed to fetch user feedback:", error);
    }
  };

  const fetchReviewMetrics = async () => {
    try {
      const metricsPromises = reviews.map(review =>
        axios.get(`${backendUrl}/api/reviews/helpful/metrics/${review._id}`)
      );
      const metricsResults = await Promise.all(metricsPromises);

      const metricsMap = {};
      metricsResults.forEach(result => {
        if (result.data.success) {
          metricsMap[result.data.data.reviewId] = {
            helpfulCount: result.data.data.helpfulCount,
            unhelpfulCount: result.data.data.unhelpfulCount,
            netHelpfulness: result.data.data.netHelpfulness,
          };
        }
      });

      setReviewMetrics(metricsMap);
      return metricsMap;
    } catch (error) {
      console.error("Failed to fetch review metrics:", error);
      return {};
    }
  };

  const handleFilter = async (filter, reviewsData = reviews) => {
    let filtered = [...reviewsData];

    if (filter === "popular") {
      const metricsMap = await fetchReviewMetrics();

      filtered = [...reviewsData].sort((a, b) => {
        const aScore = metricsMap[a._id]?.netHelpfulness || 0;
        const bScore = metricsMap[b._id]?.netHelpfulness || 0;
        return bScore - aScore;
      }).slice(0, 10);
    } else if (filter === "positive") {
      filtered = reviewsData.filter(review => review.rating >= 4);
    } else if (filter === "negative") {
      filtered = reviewsData.filter(review => review.rating <= 2);
    }

    setFilteredReviews(filtered);
    setActiveFilter(filter);
  };

  const submitReview = async (e) => {
    e.preventDefault();

    if (!token) return toast.error("You must be logged in to submit a review");
    if (hasReviewed) return toast.error("You've already reviewed this product");
    if (rating === 0) return toast.error("Please select a rating");

    try {
      const response = await axios.post(`${backendUrl}/api/review/add`, {
        name: username,
        email: useremail,
        date: Date.now(),
        rating,
        reviewtext: reviewText,
        productId,
      });

      if (response.data.success) {
        setShowSuccessModal(true);
        setRating(0);
        setReviewText("");
        fetchReviews();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to submit review");
    }
  };

  const handleHelpfulFeedback = async (reviewId, isHelpful) => {
    if (!token) {
      setShowAuthModal(true);
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            window.location.href = "/login";
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return;
    }

    try {
      const response = await axios.post(`${backendUrl}/api/reviews/helpful/add`, {
        name: username,
        email: useremail,
        date: Date.now(),
        reviewId,
        isHelpful,
      });

      if (response.data.success) {
        setUserFeedbackMap(prev => ({
          ...prev,
          [reviewId]: isHelpful
        }));

        setShowFeedbackThanks(prev => ({
          ...prev,
          [reviewId]: true
        }));

        handleFilter(activeFilter);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to submit feedback");
    }

    setTimeout(() => {
      setShowFeedbackThanks(prev => ({
        ...prev,
        [reviewId]: false
      }));
    }, 3000);
  };

  const fetchReviews = async () => {
    setIsLoading(true);
    await fetchReviewMetrics();
    await handleFilter(activeFilter);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchReviews();
  }, [productId]);

  useEffect(() => {
    if (reviews.length > 0 && useremail) {
      fetchUserFeedback();
    }
  }, [reviews, useremail]);

  const averageRating = productReviews.length > 0
    ? productReviews.reduce((sum, review) => sum + review.rating, 0) / productReviews.length
    : 0;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }


  return (
    <section className="bg-white py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <motion.h1 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-3xl font-bold text-gray-900 mb-8"
        >
          Customer Reviews
        </motion.h1>

        {/* Rating Summary */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="bg-gray-50 rounded-xl p-6 mb-8 shadow-sm"
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h2 className="text-xl font-bold text-gray-900">
                Overall Rating
              </h2>
              <div className="flex items-center justify-center md:justify-start mt-2">
                {[...Array(5)].map((star, index) => (
                  <FaStar
                    key={index}
                    className={`${
                      index < Math.round(averageRating)
                        ? "text-yellow-400"
                        : "text-gray-300"
                    } text-2xl`}
                  />
                ))}
                <span className="ml-2 text-xl font-semibold text-gray-900">
                  {reviews.length > 0
                    ? averageRating.toFixed(1)
                    : "No reviews yet"}
                </span>
                <span className="ml-2 text-sm text-gray-500">
                  ({reviews.length} {reviews.length === 1 ? 'review' : 'reviews'})
                </span>
              </div>
            </div>
            
            {/* Filter Controls */}
            <div className="text-center md:text-right">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                Filter Reviews
              </h2>
              <div className="flex flex-wrap justify-center gap-2">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleFilter("popular")}
                  className={`px-4 py-2 rounded-full text-sm ${
                    activeFilter === "popular"
                      ? "bg-gray-600 text-white"
                      : "bg-gray-200 text-gray-700"
                  }`}
                >
                  Popular
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleFilter("positive")}
                  className={`px-4 py-2 rounded-full text-sm ${
                    activeFilter === "positive"
                      ? "bg-green-600 text-white"
                      : "bg-gray-200 text-gray-700"
                  }`}
                >
                  Positive
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleFilter("negative")}
                  className={`px-4 py-2 rounded-full text-sm ${
                    activeFilter === "negative"
                      ? "bg-red-600 text-white"
                      : "bg-gray-200 text-gray-700"
                  }`}
                >
                  Negative
                </motion.button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Reviews List */}
        <div className="space-y-6">
          <AnimatePresence>
            {filteredReviews.length > 0 ? (
              filteredReviews.map((review, index) => {
                const userFeedback = userFeedbackMap[review._id];
                const isHelpful = userFeedback === true;
                const isUnhelpful = userFeedback === false;
                const hasGivenFeedback = userFeedback !== undefined;
                
                return (
                  <motion.div
                    key={review._id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                    className="bg-white rounded-xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-lg"
                  >
                    <div className="p-6">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                          <h2 className="text-lg font-semibold text-gray-900">
                            {review.name}
                          </h2>
                          <p className="text-sm text-gray-500 mt-1">
                            {new Date(review.date).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })}
                          </p>
                        </div>
                        <div className="flex">
                          {[...Array(5)].map((_, i) => (
                            <FaStar
                              key={i}
                              className={`${
                                i < review.rating ? "text-yellow-400" : "text-gray-300"
                              } text-lg`}
                            />
                          ))}
                        </div>
                      </div>
                      <p className="text-gray-700 mt-4">{review.reviewtext}</p>
                      
                      {/* Feedback Section */}
                      <div className="mt-6 pt-4 border-t border-gray-100">
                        <p className="text-sm text-gray-600 mb-2">Was this review helpful?</p>
                        <div className="flex items-center gap-3">
                          {!hasGivenFeedback ? (
                            <>
                              <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => handleHelpfulFeedback(review._id, true)}
                                className="flex items-center gap-1 px-3 py-1 bg-green-50 text-green-600 rounded-full text-sm"
                              >
                                <FaRegSmile className="text-green-600" />
                                <span>Yes</span>
                              </motion.button>
                              <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => handleHelpfulFeedback(review._id, false)}
                                className="flex items-center gap-1 px-3 py-1 bg-red-50 text-red-600 rounded-full text-sm"
                              >
                                <FaRegFrown className="text-red-600" />
                                <span>No</span>
                              </motion.button>
                            </>
                          ) : showFeedbackThanks[review._id] ? (
                            <motion.div
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              className="text-sm text-gray-500"
                            >
                              Thanks for your feedback!
                            </motion.div>
                          ) : (
                            <div className="text-sm text-gray-500">
                              You marked this review as {isHelpful ? "helpful" : "not helpful"}
                            </div>
                          )}
                          <span className="text-xs text-gray-400 ml-auto">
                            {review.helpfulCount > 0 && (
                              <>{review.helpfulCount} found this helpful</>
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-10 bg-gray-50 rounded-xl"
              >
                <p className="text-gray-500">No reviews match your filter</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Review Form Section */}
        {!hasReviewed && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mt-12 bg-gray-50 rounded-xl p-8"
          >
            <div className="max-w-2xl mx-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Add Your Review</h2>
              <p className="text-gray-600 mb-6">Share your experience with this product</p>
              
              <form onSubmit={submitReview} className="space-y-6">
                <div>
                  <p className="text-lg font-medium mb-3">Your Rating</p>
                  <div className="flex gap-2">
                    {[...Array(5)].map((star, index) => {
                      const ratingValue = index + 1;
                      return (
                        <motion.button
                          key={index}
                          type="button"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          className="focus:outline-none"
                          onClick={() => setRating(ratingValue)}
                          onMouseEnter={() => setHover(ratingValue)}
                          onMouseLeave={() => setHover(0)}
                        >
                          <FaStar
                            className={`text-3xl ${
                              ratingValue <= (hover || rating)
                                ? "text-yellow-400"
                                : "text-gray-300"
                            }`}
                          />
                        </motion.button>
                      );
                    })}
                  </div>
                </div>
                
                <div>
                  <label htmlFor="review" className="block text-lg font-medium text-gray-700 mb-2">
                    Your Review
                  </label>
                  <textarea
                    id="review"
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Share details about your experience with this product..."
                    value={reviewText}
                    onChange={(e) => setReviewText(e.target.value)}
                    required
                  />
                </div>
                
                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full sm:w-auto px-6 py-3 bg-gray-800 text-white font-medium rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Submit Review
                </motion.button>
              </form>
            </div>
          </motion.div>
        )}
      </div>

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Review Submitted Successfully!"
        message="Thank you for your review! Your feedback helps other customers make informed decisions."
      />

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => {
          setShowAuthModal(false);
          setCountdown(10);
        }}
        redirectPath="/login"
        countdownStart={10}
      />
    </section>
  );
};

export default Reviews;